<template>
  <div>
    <integratedSearchBar
      v-if="currentUserCan('create', 'contract')"
      :button_display="true"
      button_type="link"
      :button_parameter="{ name: 'newContract' }"
      button_label="Create a new contract"
      v-bind:search.sync="search"
      search_label="Search contracts..."
    />

    <v-data-table
      :headers="headers"
      :items="contracts"
      :search="search"
      :pagination.sync="pagination"
      :loading="loading"
      rows-per-page-text="Contracts per page"
    >
      <v-progress-linear
        slot="progress"
        color="blue"
        indeterminate
      ></v-progress-linear>
      <template slot="items" slot-scope="props">
        <td
          style="white-space: nowrap; height: 36px;"
          @click="redirectToContract(props.item.id)"
        >
          {{ props.item.name }}
        </td>

        <td
          style="white-space: nowrap; height: 36px;"
          @click="redirectToContract(props.item.id)"
        >
          {{ props.item.supplier_company_name }}
        </td>

        <td
          style="white-space: nowrap; height: 36px;"
          @click="redirectToContract(props.item.id)"
        >
          {{ props.item.customer_company_name }}
        </td>

        <td
          style="white-space: nowrap; height: 36px;"
          @click="redirectToContract(props.item.id)"
        >
          <v-layout justify-begin>
            <chipList :list="productNames(props.item)" />
          </v-layout>
        </td>

        <td
          style="white-space: nowrap; height: 36px;"
          @click="redirectToContract(props.item.id)"
          v-if="status != 'pending'"
        >
          <v-layout align-center>
            <span class="pr-2">
              {{ props.item.bank_invoices.length }} /
              {{ nbInvoiceExpected(props.item) }}
            </span>
            <v-label
              >{{ nbInvoiceReconciliated(props.item) }} reconciled</v-label
            >
          </v-layout>
        </td>

        <td
          style="white-space: nowrap; height: 36px;"
          class="min-width clickable px-0"
        >
          <v-layout justify-center align-center row>
            <div>
              <v-layout row>
                <v-tooltip top>
                  <v-btn
                    icon
                    small
                    flat
                    v-if="contractValidated()"
                    :to="{ name: 'expeditions', params: { id: props.item.id } }"
                    slot="activator"
                  >
                    <v-icon small>mdi-ferry</v-icon>
                  </v-btn>
                  <span>Expeditions</span>
                </v-tooltip>
                <v-tooltip top>
                  <v-btn
                    small
                    icon
                    flat
                    @click="duplicateContract(props.item.id)"
                    slot="activator"
                  >
                    <v-icon small color="theme_primary">file_copy</v-icon>
                  </v-btn>
                  <span>Duplicate Contract</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <destroyModale
                    resourceName="contract"
                    :itemId="props.item.id"
                    warningText="You are going to remove this Contract and its associated contacts and products permanently"
                    redirectRoute="contracts"
                    slot="activator"
                    icon
                    :fab="false"
                    :show_label="false"
                    :flat="true"
                    :small="true"
                    :small_icon="true"
                  />
                  <span>Delete</span>
                </v-tooltip>
              </v-layout>
            </div>
          </v-layout>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import integratedSearchBar from "../shared/integratedSearchBar";
import CONTRACTS_INDEX from "../../graphql/contracts/index.gql";
import DUPLICATE_CONTRACT from "../../graphql/contracts/duplicate.gql";
import date from "../../mixins/date.js";
import { mapGetters } from "vuex";

export default {
  mixins: [date],
  components: {
    integratedSearchBar,
  },
  props: {
    status: {
      type: String,
    },
    archived: {
      type: Boolean,
    },
  },
  data() {
    return {
      search: "",
      loading: true,
      pagination: {
        descending: false,
        rowsPerPage: 25,
        sortBy: "name",
      },
      baseHeaders: [
        {
          text: "Name",
          align: "left",
          sortable: true,
          value: "name",
        },
        {
          text: "Supplier",
          align: "left",
          sortable: true,
          value: "supplier_company_name",
        },
        {
          text: "Customer",
          align: "left",
          sortable: true,
          value: "customer_company_name",
        },
        {
          text: "Products to trade",
          align: "left",
          sortable: true,
          value: "products_to_trade_names",
        },
        {
          text: "Uploaded Invoices",
          align: "left",
          sortable: true,
          value: "",
        },
        {
          text: "Actions",
          align: "center",
          sortable: false,
        },
      ],
    };
  },
  computed: {
    ...mapGetters("contractStore", [
      "pendingContracts",
      "validatedContracts",
      "archivedContracts",
    ]),
    contracts() {
      if (this.archived) return this.archivedContracts;
      if (this.status == "pending") return this.pendingContracts;
      else return this.validatedContracts;
    },
    headers() {
      if (this.status == "pending") {
        this.baseHeaders.splice(4, 1);
        this.baseHeaders.splice(5, 1);
        return this.baseHeaders;
      }
      return this.baseHeaders;
    },
  },
  methods: {
    contractValidated() {
      return this.status == "validated";
    },
    productNames(contract) {
      var productTrades = contract.product_trades.filter((e) => {
        return e.previsional === (this.status === "pending");
      });

      if (productTrades.length === 0) return [];

      return productTrades.map((e) => e.product.name);
    },
    redirectToContract(id) {
      this.$router.push({ name: "contract", params: { id: id } });
    },
    duplicateContract(id) {
      this.sendMutation("duplicated", "contract", DUPLICATE_CONTRACT, {
        id: id,
      });
    },
    nbInvoiceExpected(contract) {
      let nb_expected = 0;
      let nb_expeditions = contract.expeditions
        .filter((e) => e.previsional === false)
        .forEach((exp) => {
          nb_expected += 2 + exp.score_costs.length;
        });

      let toAdd = [
        new Set(
          contract.bank_invoice_links
            .filter((e) => e.origin == "custom")
            .map((e) => e.bank_invoice.id)
        ),
      ].length;
      return nb_expected + toAdd;
    },
    nbInvoiceReconciliated(contract) {
      if (!contract.bank_invoices) return 0;

      return contract.bank_invoices.filter((e) => e.bank_reconciliation_id)
        .length;
    },
  },
  created() {
    this.fetchAndSubscribe(CONTRACTS_INDEX, null, "contractStore", "contracts")
      .then(() => {
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
      });
  },
  beforeDestroy() {
    this.unsubscribe("contracts");
  },
};
</script>

<style lang="stylus" scoped>
td {
  cursor: pointer;
}
</style>
